/**
 * Permission Helper Functions for Role-Based Access Control
 *
 * Role Hierarchy (highest to lowest privilege):
 * - Admin: Full access including data refresh from DealerBuilt
 * - CEO: Executive access + AI Chat
 * - Executive: Everything except data refresh
 * - GeneralManager: No unlock projections, view-only config
 * - DepartmentManager: Single location, view-only projections, limited pages
 * - MarketingManager: Reports page only (Marketing tab)
 * - Unassigned: No access
 */

import { UserRole } from './msalConfig';

/**
 * Can refresh data from DealerBuilt database (Load from DB buttons)
 * Only Admin has this permission
 */
export const canRefreshData = (role: UserRole | null): boolean => {
  return role === 'Admin';
};

/**
 * Can unlock projections (allow editing after lock date)
 * Admin and Executive only
 */
export const canUnlockProjections = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive';
};

/**
 * Can edit configuration (working days, holidays)
 * Admin and Executive only
 */
export const canEditConfig = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive';
};

/**
 * Can view the configuration page.
 * Admin/CEO/Executive (edit) and GeneralManager (view-only) see every tab.
 * A DepartmentManager with NEW_VEHICLE department access sees ONLY the
 * Sales Teams tab (Configuration.tsx hides the rest); other DeptMgrs are
 * blocked from the page entirely.
 */
export const canViewConfig = (
  role: UserRole | null,
  assignedDepartmentIds?: string[],
): boolean => {
  if (role === 'Admin' || role === 'CEO' || role === 'Executive' || role === 'GeneralManager') {
    return true;
  }
  return canEditSalesTeamsConfig(role, assignedDepartmentIds);
};

/**
 * Can edit Sales Teams config (Configuration → Sales Teams; SALES_TEAM_ASSIGNMENT
 * items). Admin/CEO/Executive, GeneralManagers, and DepartmentManagers holding
 * NEW_VEHICLE — they build the teams behind the Sales Team Report. The backend
 * mirror lives in lambda/shared/config-access.ts (save-config/delete-config),
 * which is the real enforcement boundary. This is the one config tab a GM can
 * edit; the rest of the Configuration page stays view-only for them.
 */
export const canEditSalesTeamsConfig = (
  role: UserRole | null,
  assignedDepartmentIds?: string[],
): boolean => {
  if (canEditConfig(role) || role === 'GeneralManager') return true;
  return role === 'DepartmentManager' && !!assignedDepartmentIds?.includes('NEW_VEHICLE');
};

/**
 * Can edit projections (modify projection values)
 * Admin, Executive, and GeneralManager
 * DepartmentManager can only view projections
 */
export const canEditProjections = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive' || role === 'GeneralManager';
};

/**
 * Can view historical data page
 * Admin, Executive, and GeneralManager
 * DepartmentManager is blocked from this page
 */
export const canViewHistorical = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive' || role === 'GeneralManager';
};

/**
 * Can adjust a saved projection's monthly spread after seasonality (Executive monthly
 * adjustments). Admin, CEO and Executive only; the backend enforces the same list.
 */
export const canAdjustProjectionMonths = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive';
};

/**
 * Can connect a private AI agent with an agent access key (Agent Access page).
 * Admin, CEO and Executive only (D3 in docs/plans/agent-access-keys.md); the backend
 * enforces the same list in lambda/shared/agent-key.ts.
 */
export const canUseAgentAccess = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive';
};

/**
 * Can view users/audit logs page
 * Admin and Executive only
 */
export const canViewUsers = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive';
};

/**
 * Is the user restricted to assigned location(s)?
 * DepartmentManager and TeamServiceManager can only see their assigned locations
 */
export const isLocationRestricted = (role: UserRole | null): boolean => {
  return role === 'DepartmentManager' || role === 'TeamServiceManager' || role === 'FinanceManager';
};

/**
 * Is the user a Team Service Manager? (Forecast-only, SERVICE dept, auto-assigned location)
 */
export const isTeamServiceManager = (role: UserRole | null): boolean => {
  return role === 'TeamServiceManager';
};

/**
 * Can access the application at all
 * Everyone except Unassigned
 */
export const canAccessApp = (role: UserRole | null): boolean => {
  return role !== null && role !== 'Unassigned';
};

/**
 * Can access the Insurance Lead Queue module
 * Admin, Executive, and Insurance agents only
 * Other roles (GeneralManager, DepartmentManager) cannot access Insurance
 */
export const canAccessInsurance = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive' || role === 'Insurance';
};

/**
 * Check if user is Insurance-only (no access to other dashboard features)
 * Insurance role users should only see the Insurance tab
 */
export const isInsuranceOnly = (role: UserRole | null): boolean => {
  return role === 'Insurance';
};

/**
 * Check if user is MarketingManager-only (Reports page with Marketing tab only)
 */
export const isMarketingManagerOnly = (role: UserRole | null): boolean => {
  return role === 'MarketingManager';
};

/**
 * Check if user is Accounting-only (Nut Sheet page only, all locations).
 * Like MarketingManager/Insurance, a single-page role outside the main hierarchy.
 */
export const isAccountingOnly = (role: UserRole | null): boolean => {
  return role === 'Accounting';
};

/**
 * Check if user is FinanceManager-only (F&I): Nut Sheet page only, scoped to assigned
 * location(s). Like Accounting it's a single-page role outside the main hierarchy, but
 * location-restricted (the backend resolveNutSheetAccess scopes them to assignedLocationIds).
 */
export const isFinanceManagerOnly = (role: UserRole | null): boolean => {
  return role === 'FinanceManager';
};

/**
 * Can access the main dashboard features (Forecast, Projections, etc.)
 * Everyone except Insurance-only, MarketingManager-only, Accounting-only, and Unassigned
 */
export const canAccessDashboard = (role: UserRole | null): boolean => {
  return role !== null && role !== 'Unassigned' && role !== 'Insurance' && role !== 'MarketingManager' && role !== 'Accounting' && role !== 'FinanceManager';
};

/**
 * Can access the Reports page
 * All dashboard users (Admin, Executive, GeneralManager, DepartmentManager)
 * Insurance-only users use the Insurance module's Reports tab instead
 */
export const canAccessReports = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive' || role === 'GeneralManager' || role === 'DepartmentManager' || role === 'MarketingManager' || role === 'TeamServiceManager';
};

/**
 * Can access the AI Chat assistant
 * Admin, CEO, and Executive roles
 */
export const canAccessAIChat = (role: UserRole | null): boolean => {
  return role === 'Admin' || role === 'CEO' || role === 'Executive';
};

/**
 * Single source of truth for Nut Sheet page access (UX gate). Admin/CEO/Executive,
 * GeneralManager and Accounting always qualify (all locations). A DepartmentManager
 * qualifies only when they hold NEW_VEHICLE and/or USED_VEHICLE department access
 * (either grants the whole sheet). The backend mirror — resolveNutSheetAccess in
 * lambda/nut-sheet/shared/access.ts — is the real enforcement boundary and also
 * scopes DepartmentManagers to their assigned locations.
 */
export const canAccessNutSheet = (
  role: UserRole | null,
  assignedDepartmentIds?: string[],
): boolean => {
  if (
    role === 'Admin' ||
    role === 'CEO' ||
    role === 'Executive' ||
    role === 'GeneralManager' ||
    role === 'Accounting' ||
    role === 'FinanceManager'
  ) {
    return true;
  }
  if (role === 'DepartmentManager') {
    return !!assignedDepartmentIds?.some(
      (d) => d === 'NEW_VEHICLE' || d === 'USED_VEHICLE',
    );
  }
  return false;
};

/**
 * Permission matrix for reference:
 *
 * | Feature              | Admin | CEO | Exec | GM  | DeptMgr | TSM | Insurance | MktgMgr  |
 * |----------------------|-------|-----|------|-----|---------|-----|-----------|----------|
 * | Refresh Data         | Yes   | No  | No   | No  | No      | No  | No        | No       |
 * | Unlock Projections   | Yes   | Yes | Yes  | No  | No      | No  | No        | No       |
 * | Edit Configuration   | Yes   | Yes | Yes  | No (6) | No (6) | No | No       | No       |
 * | View Configuration   | Yes   | Yes | Yes  | Yes | No (6)  | No  | No        | No       |
 * | Edit Projections     | Yes   | Yes | Yes  | Yes | No      | No  | No        | No       |
 * | View Historical      | Yes   | Yes | Yes  | Yes | No      | No  | No        | No       |
 * | View Users/Audit     | Yes   | Yes | Yes  | No  | No      | No  | No        | No       |
 * | View Reports         | Yes   | Yes | Yes  | Yes | Yes     | Yes (5) | No    | Yes (2)  |
 * | Forecast Page        | Yes   | Yes | Yes  | Yes | Yes     | Yes | No        | Yes (3)  |
 * | SA/Tech Performance  | Yes   | Yes | Yes  | Yes | Yes     | Yes | No        | Yes      |
 * | Insurance Module     | Yes   | Yes | Yes  | No  | No      | No  | Yes       | No       |
 * | AI Chat              | Yes   | Yes | Yes  | No  | No      | No  | No        | No       |
 * | All Locations        | Yes   | Yes | Yes  | Yes | No (1)  | No  | N/A       | Yes (4)  |
 *
 * (2) MarketingManager sees only the Marketing tab on the Reports page
 * (3) MarketingManager sees all depts except ALL/OTHERS; Expense/Profit hidden on tiles; no Consolidated
 * (5) TeamServiceManager sees only the Service tab and only the TSM Group Ranking + TSM Items + Trade-In reports
 * (4) All 10 individual locations, no Consolidated view
 * (6) Sales Teams tab exception (canEditSalesTeamsConfig): GeneralManagers can
 *     edit that one tab (everything else stays view-only for them), and a
 *     DepartmentManager with NEW_VEHICLE sees the Configuration page with ONLY
 *     the Sales Teams tab, which they can edit
 *
 * Nut Sheet access (canAccessNutSheet): Admin/CEO/Exec (all locations),
 * GeneralManager (all locations), DepartmentManager with NEW_VEHICLE and/or
 * USED_VEHICLE (their assigned locations only), the single-purpose Accounting
 * role (all locations, Nut Sheet is the only page they can see), and the
 * single-purpose FinanceManager (F&I) role (Nut Sheet only, scoped to their
 * assigned locations — no department gate; sees nothing until assigned).
 */
